import React, { Suspense } from 'react';
import lazyWithRetry from './utils/lazyWithRetry';
import ChunkErrorBoundary from './components/ChunkErrorBoundary/ChunkErrorBoundary';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PrivateRoute } from './components/PrivateRoute/PrivateRoute';
import Error from './components/layouts/Error/Error';

import PaymentStatusPage from './pages/BuyChallenge/PaymentStatusPage';

import ResetPassword from './features/register/pages/ResetPassword/ResetPassword';

import Login from './features/register/pages/Login/Login';
import { useAuth } from './context/AuthContext/AuthContext';
import { useAffiliationType } from './features/partner/hooks/useAffiliationType';
import Loader from './components/Loader/Loader';
import ForgotPassword from './features/register/pages/ForgotPassword/ForgotPassword';
import SsoPage from './pages/user/Sso/SsoPage';

// Lazy-loaded page components
const Download = lazyWithRetry(() => import('./pages/Download/Download'), 'Download');
const Profile = lazyWithRetry(() => import('./pages/user/profil'), 'profil');
const ClientArea = lazyWithRetry(() => import('./features/area/pages/ClientArea/ClientArea'), 'ClientArea');
const TraderArea = lazyWithRetry(() => import('./features/area/pages/TraderArea/TraderArea'), 'TraderArea');
const InstantFundedArea = lazyWithRetry(() => import('./features/area/pages/InstantFundedArea/InstantFundedArea'), 'InstantFundedArea');
const FundedArea = lazyWithRetry(() => import('./features/area/pages/FundedArea/FundedArea'), 'FundedArea');
const EditEmail = lazyWithRetry(() => import('./pages/Admin/EditMail/EditMail'), 'EditMail');
const Certificate = lazyWithRetry(() => import('./pages/user/certificate'), 'certificate');
const Store = lazyWithRetry(() => import('./features/store/pages/Store/Store'), 'Store');
const BuyFunded = lazyWithRetry(() => import('./features/buyFunded/pages/BuyFunded/BuyFunded'), 'BuyFunded');
const Dashboard = lazyWithRetry(() => import('./features/admin/pages/Dashboard/Dashboard'), 'Dashboard');
const LightDashboard = lazyWithRetry(() => import('./features/admin/pages/LightDashboard/LightDashboard'), 'LightDashboard');
const TimeTracker = lazyWithRetry(() => import('./pages/Admin/TimeTracker'), 'TimeTracker');
const CidReport = lazyWithRetry(() => import('./pages/Admin/CidReport/CidReport'), 'CidReport');
const Reports = lazyWithRetry(() => import('./pages/Admin/Reports/Reports'), 'Reports');
const Kyc = lazyWithRetry(() => import('./pages/Admin/Kyc/Kyc'), 'Kyc');
const Challenges = lazyWithRetry(() => import('./pages/Admin/Challenges/Challenges'), 'Challenges');
const Funded = lazyWithRetry(() => import('./pages/Admin/Funded/Funded'), 'Funded');
const PspManagement = lazyWithRetry(() => import('./pages/Admin/pspManagement/PspManagement'), 'PspManagement');
const SettingsManager = lazyWithRetry(() => import('./pages/Admin/SettingsManager/SettingsManager'), 'SettingsManager');
const CalendarManagement = lazyWithRetry(() => import('./pages/Admin/CalendarManagement/CalendarManagement'), 'CalendarManagement');
const EmailManagement = lazyWithRetry(() => import('./pages/Admin/EmailManagement/EmailManagement'), 'EmailManagement');
const CashManagementActive = lazyWithRetry(() => import('./pages/Admin/CashManagement/Active/Active'), 'Active');
const CashManagementHistory = lazyWithRetry(() => import('./pages/Admin/CashManagement/History/CashManagementHistory'), 'CashManagementHistory');
const CashManagementAffiliate = lazyWithRetry(() => import('./pages/Admin/CashManagement/Affiliate/Affiliate'), 'Affiliate');
const Users = lazyWithRetry(() => import('./pages/Admin/Users/Users'), 'Users');
const Apply = lazyWithRetry(() => import('./features/application/pages/Apply/Apply'), 'Apply');
const AdminApplications = lazyWithRetry(() => import('./pages/Admin/Applications/Applications'), 'Applications');
const Affiliates = lazyWithRetry(() => import('./pages/Admin/Affiliates/Affiliates'), 'Affiliates');
const Calendar = lazyWithRetry(() => import('./pages/user/calendar'), 'calendar');
const Partner = lazyWithRetry(
  () => import('./features/partner/partner').then((m) => ({ default: m.Partner })),
  'partner',
);
const Webtrader = lazyWithRetry(() => import('./pages/Webtrader/Webtrader'), 'Webtrader');
const TransactionHistory = lazyWithRetry(() => import('./pages/user/transactions/history'), 'history');
const TransactionStatusPage = lazyWithRetry(() => import('./pages/user/TransactionStatus/TransactionStatusPage'), 'TransactionStatusPage');
const SwitchRFX = lazyWithRetry(() => import('./pages/user/switchRfx/SwitchRFX'), 'SwitchRFX');

const ROUTES = {
  UNAUTHORIZED: '/unauthorized',
  ADMIN_CASH_MANAGEMENT_ACTIVE: '/admin/cash-management/active',
} as const;

export default function Router() {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions, isPermissionsLoading, isProfileLoading, profile, user } =
    useAuth();
  const { t } = useTranslation([
    'unauthorized',
    'comingSoon',
    'notFound',
    'accessDenied',
  ]);
  // Partner area: clients with an affiliation deal only (see useAffiliationType).
  const { hasAffiliationDeal, loading: isAffiliationLoading } = useAffiliationType(
    Boolean(user) && user?.role !== 'ROLE_ADMIN',
  );

  if (isPermissionsLoading || isProfileLoading) {
    return <Loader />;
  }

  const getPartnerAreaElement = () => {
    if (isAffiliationLoading) return <Loader />;
    if (hasAffiliationDeal) return <Partner />;
    return <Navigate to="/" replace />;
  };

  const hasDashboardAccess =
    Array.isArray(permissions) && permissions.includes('ACCESS_DASHBOARD');

  const hasActiveChallenge = profile?.hasActiveChallenge ?? false;
  const hasActiveFundedChallenge = profile?.hasActiveFundedChallenge ?? false;

  const getClientAreaElement = () => {
    if (hasActiveChallenge) return <ClientArea />;
    return <Navigate to="/" replace />;
  };

  const getTraderAreaElement = () => {
    if (hasActiveFundedChallenge) return <TraderArea />;
    return <Navigate to="/" replace />;
  };

  const hasViewCidReport =
    Array.isArray(permissions) && permissions.includes('VIEW_CID_REPORT');

  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<Loader />}>
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/resetpassword" element={<ResetPassword />} />
        <Route path="/login" element={<Login />} />
        {/* SSO landing page for "Sign in with RaiseFX" — no auth guard required */}
        <Route path="/sso" element={<SsoPage />} />
        {/* Self-serve registration is replaced by the funded application funnel.
            The Register page code is kept (features/register) but the route now
            redirects to /apply, preserving affiliate / utm / capital query params. */}
        <Route
          path="/register"
          element={<Navigate to={{ pathname: '/apply', search: location.search }} replace />}
        />
        {/* Funded application funnel — public entry point (creates the account at step 1) */}
        <Route path="/apply" element={<Apply />} />
        <Route
          path="/buy-challenge/payment-status/:paymentId"
          element={<PaymentStatusPage />}
        />
        <Route path="/" element={<PrivateRoute />}>
          <Route index element={<FundedArea />} />
          <Route path="/switch-rfx" element={<SwitchRFX />} />

          <Route path="/client-area" element={getClientAreaElement()} />
          <Route path="/trader-area" element={getTraderAreaElement()} />
          <Route path="/funded-area" element={<InstantFundedArea />} />

          <Route path="/partner-area" element={getPartnerAreaElement()} />
          <Route path="/download" element={<Download />} />
          <Route path="/economic-calendar" element={<Calendar />} />
          <Route path="/certificate" element={<Certificate />} />
          <Route path="/store" element={<Store />} />
          <Route path="/buy-funded" element={<BuyFunded />} />
            <Route path="/webtrader" element={<Webtrader />} />

          {/* Routes accessible to everyone authenticated */}
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/kyc" element={<Profile />} />
          <Route path="/profile/two-authentication" element={<Profile />} />
          <Route
            path="/transaction-status"
            element={<TransactionStatusPage />}
          />
          <Route
            path="/transactions/history"
            element={<TransactionHistory />}
          />

          <Route
            path="/buy-challenge/payment-status"
            element={<PaymentStatusPage />}
          />
          <Route
            path="/calculator"
            element={
              <Error
                documentTitle={t('documentTitle', { ns: 'comingSoon' })}
                title={t('title', { ns: 'comingSoon' })}
                subtitle={t('text', { ns: 'comingSoon' })}
                button={t('button', { ns: 'comingSoon' })}
                onClick={() => navigate('/')}
              />
            }
          />

          {/* Admin Routes */}
          <Route path="admin" element={<PrivateRoute roles={['ROLE_ADMIN']} />}>
            <Route
              path="/admin/dashboard"
              element={hasDashboardAccess ? <Dashboard /> : <LightDashboard />}
            />
            <Route
              path="/admin/time-tracker"
              element={
                hasDashboardAccess ? (
                  <TimeTracker />
                ) : (
                  <Navigate to={ROUTES.UNAUTHORIZED} />
                )
              }
            />
            <Route
              path="/admin/cid-report"
              element={
                hasViewCidReport ? (
                  <CidReport />
                ) : (
                  <Navigate to={ROUTES.UNAUTHORIZED} />
                )
              }
            />
            <Route path="/admin/users" element={<Users />} />
            <Route
              path="/admin/cash-management"
              element={
                <Navigate to={ROUTES.ADMIN_CASH_MANAGEMENT_ACTIVE} replace />
              }
            />
            <Route
              path="/admin/cash-management/active"
              element={<CashManagementActive />}
            />
            <Route
              path="/admin/cash-management/history"
              element={<CashManagementHistory />}
            />
            <Route
              path="/admin/cash-management/affiliate"
              element={<CashManagementAffiliate />}
            />
            <Route path="/admin/reports" element={<Reports />} />
            <Route path="/admin/affiliates" element={<Affiliates />} />
            <Route path="/admin/kyc" element={<Kyc />} />
            <Route path="/admin/challenges" element={<Challenges />} />
            <Route path="/admin/funded" element={<Funded />} />
            <Route path="/admin/applications" element={<AdminApplications />} />
            <Route
              path="/admin/email-management"
              element={<EmailManagement />}
            />
            <Route
              path="/admin/broker/psp-management"
              element={<PspManagement />}
            />
            <Route
              path="/admin/broker/settings"
              element={<SettingsManager />}
            />
            <Route
              path="/admin/broker/calendar-management"
              element={<CalendarManagement />}
            />
            <Route
              path="/admin/email-management/edit/:templateId"
              element={<EditEmail />}
            />
          </Route>
        </Route>

        {/* Error Fallbacks */}
        <Route
          path="/unauthorized"
          element={
            <Error
              documentTitle={t('documentTitle')}
              title={t('title')}
              subtitle={t('text')}
              button={t('button')}
              onClick={() => navigate('/')}
            />
          }
        />
        <Route
          path="*"
          element={
            <Error
              documentTitle={t('documentTitle', { ns: 'notFound' })}
              title={t('title', { ns: 'notFound' })}
              subtitle={t('text', { ns: 'notFound' })}
              button={t('button', { ns: 'notFound' })}
              onClick={() => navigate('/')}
            />
          }
        />
      </Routes>
      </Suspense>
    </ChunkErrorBoundary>
  );
}
